import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CustomCard from '@components/CustomCard';
import {
  Form,
  Row,
  Col,
  Button,
  Divider,
  Input,
  Radio,
  Switch,
  message,
  Spin,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import useSwr from '@hooks/useSwr';
import { QuestionCircleOutlined } from '@ant-design/icons';

type Payload = {
  mailType: 'SMTP' | 'SENDGRID';
  host: string;
  port: number;
  fromDisplayName: string;
  from: string;
  replyToDisplayName: string;
  replyTo: string;
  envelopeFrom: string;
  enableSsl: boolean;
  enableStartTls: boolean;
  enableAuthentication: boolean;
  username: string;
  password: string;
  apiKey: string;
  fromSMTP?: string;
  fromSENDGRID?: string;
};

type FormItem = {
  name: string;
  el: ReactNode;
  label?: string;
  hasDivider?: boolean;
  dividerTitle?: string;
  valuePropName?: 'checked';
  col?: {
    xs: number;
    sm: number;
  };
};

type FormItems = {
  ['SMTP']: FormItem[];
  ['SENDGRID']: FormItem[];
};

const SendEmails = () => {
  const { t } = useTranslation('main');
  const [mailType, setMailType] = useState<
    'SMTP' | 'SENDGRID' | 'PARENT' | 'NONE'
  >('NONE');
  const [isChanging, setIsChanging] = useState(false);

  const [form] = Form.useForm<Payload | undefined>();
  const formValues = Form.useWatch([], form);

  const { data, mutate } = useSwr('/send-email-configuration');

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, [`from${data.mailType}`]: data.from });
      setMailType(data.mailType);
    }
  }, [data, form]);

  const handleSendByParent = useCallback(async () => {
    if (mailType === 'PARENT') return;
    setIsChanging(true);
    try {
      await api.post('/send-email-configuration', {
        mailType: 'PARENT',
      });
      mutate({ mailType: 'PARENT' });
    } catch {
      message.error(t('personalization.unexpectedError'));
    }
    setIsChanging(false);
  }, [mailType, mutate, t]);

  const handleSendDisable = useCallback(async () => {
    if (mailType === 'NONE') return;
    setIsChanging(true);
    try {
      await api.delete('/send-email-configuration');
      mutate({ mailType: 'NONE' });
    } catch {
      message.error(t('personalization.unexpectedError'));
    }
    setIsChanging(false);
  }, [mailType, mutate, t]);

  const treatedPayload = useCallback(
    (values: Payload | undefined) => {
      const payload = {
        ...values,
        mailType: mailType as Payload['mailType'],
      };

      if (mailType === 'SMTP') {
        payload.enableSsl = !!values?.enableSsl;
        payload.enableStartTls = !!values?.enableStartTls;
        payload.enableAuthentication = !!values?.enableAuthentication;
        payload.from = values?.fromSMTP;
        delete payload.fromSMTP;
      } else {
        payload.from = values?.fromSENDGRID;
        delete payload.fromSENDGRID;
      }

      return payload;
    },
    [mailType],
  );

  const handleTestConnection = useCallback(async () => {
    try {
      await api.post(
        '/send-email-configuration/test',
        treatedPayload(formValues),
      );

      message.success(t('personalization.connectionSuccess'));
    } catch ({ response }: any) {
      // @ts-ignore
      const status = response?.status;
      if (status === 401) {
        message.error(t('personalization.connectionError'));
      } else {
        message.error(t('personalization.unexpectedError'));
      }
    }
  }, [formValues, t, treatedPayload]);

  const handleSaving = useCallback(
    async (values: Payload | undefined) => {
      setIsChanging(true);
      try {
        const payload = treatedPayload(values);

        await api.post('/send-email-configuration', payload);

        form.resetFields();

        mutate(payload);
        message.success(t('personalization.savedSuccessfully'));
      } catch {
        message.error(t('personalization.errorWhenSaving'));
      }
      setIsChanging(false);
    },
    [form, mutate, t, treatedPayload],
  );

  const formItems = useMemo(
    (): FormItems => ({
      SMTP: [
        {
          name: 'host',
          el: <Input />,
        },
        {
          name: 'port',
          el: <Input />,
        },
        {
          name: 'fromDisplayName',
          el: <Input />,
          hasDivider: true,
          dividerTitle: 'emails',
        },
        {
          name: 'fromSMTP',
          label: 'from',
          el: <Input />,
        },
        {
          name: 'replyToDisplayName',
          el: <Input />,
        },
        {
          name: 'replyTo',
          el: <Input />,
        },
        {
          name: 'envelopeFrom',
          el: <Input />,
        },
        {
          name: 'enableSsl',
          el: <Switch />,
          hasDivider: true,
          dividerTitle: 'config',
          valuePropName: 'checked',
          col: {
            xs: 16,
            sm: 5,
          },
        },
        {
          name: 'enableStartTls',
          el: <Switch />,
          valuePropName: 'checked',
          col: {
            xs: 16,
            sm: 5,
          },
        },
        {
          name: 'enableAuthentication',
          el: <Switch />,
          valuePropName: 'checked',
          col: {
            xs: 16,
            sm: 5,
          },
        },
        {
          name: 'username',
          el: <Input />,
          hasDivider: true,
          dividerTitle: 'credentials',
        },
        {
          name: 'password',
          el: <Input.Password placeholder="**********" />,
        },
      ],
      SENDGRID: [
        {
          name: 'fromSENDGRID',
          label: 'from',
          el: <Input />,
        },
        {
          name: 'apiKey',
          el: <Input />,
        },
      ],
    }),
    [],
  );

  return (
    <CustomCard
      title={<>{t('personalization.sendEmails')}</>}
      bodyStyle={{
        padding: 40,
      }}
      extra={isChanging && <Spin />}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '40px 0',
        }}
      >
        <Radio.Group value={mailType}>
          <Radio.Button
            value="SMTP"
            onClick={() => setMailType('SMTP')}
            disabled={isChanging}
          >
            SMTP
          </Radio.Button>
          <Radio.Button
            value="SENDGRID"
            onClick={() => setMailType('SENDGRID')}
            disabled={isChanging}
          >
            SendGrid
          </Radio.Button>
          <Radio.Button
            value="PARENT"
            onClick={handleSendByParent}
            disabled={isChanging}
          >
            {t('personalization.sendBySupplier')}
            <Button
              type="link"
              size="small"
              style={{ marginLeft: 4 }}
              icon={
                <Tooltip title={t('personalization.sendBySupplierHelp')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              }
            />
          </Radio.Button>
          <Radio.Button
            value="NONE"
            onClick={handleSendDisable}
            disabled={isChanging}
          >
            {t('personalization.disabled')}
          </Radio.Button>
        </Radio.Group>
      </div>
      {['SMTP', 'SENDGRID'].includes(mailType) && (
        <Form
          form={form}
          id="personalizationForm"
          layout="vertical"
          onFinish={handleSaving}
        >
          <Row gutter={[16, 16]} justify="center">
            {formItems?.[mailType as 'SMTP' | 'SENDGRID']?.map(
              ({
                name,
                el,
                label,
                hasDivider,
                dividerTitle,
                valuePropName,
                col,
              }) => (
                <>
                  {hasDivider && (
                    <Divider style={{ opacity: 0.8 }}>
                      {t(`personalization.${dividerTitle}`)}
                    </Divider>
                  )}
                  <Col key={name} {...(col ?? { xs: 24, sm: 12 })}>
                    <Form.Item
                      name={name}
                      label={t(`personalization.${label ?? name}`)}
                      valuePropName={valuePropName}
                    >
                      {el}
                    </Form.Item>
                  </Col>
                </>
              ),
            )}
          </Row>

          <Divider style={{ marginTop: 40 }} />
          <Row justify="end">
            <Button onClick={handleTestConnection} style={{ marginRight: 8 }}>
              {t('personalization.testConnection')}
            </Button>

            <Button type="primary" htmlType="submit" disabled={isChanging}>
              {t(`personalization.${isChanging ? 'saving' : 'save'}`)}
            </Button>
          </Row>
        </Form>
      )}
    </CustomCard>
  );
};

export default SendEmails;
